import { AppAreaConfig, DomainConfig } from './@types'
import { PaywallRoute, UpsellRoute } from '@/@types/router-types'
import { APP_AREAS } from '@/funnels/areas'

// Runtime type guard
export const isAppAreaConfig = (config: unknown): config is AppAreaConfig => {
	return (
		typeof config === 'object' && config !== null && 'appArea' in config && 'landing' in config && 'funnel' in config
	)
}

// Default values from main funnel for redirect funnels fallbacks
const DEFAULT_PAYWALL_CONFIG: { routeName: PaywallRoute } = {
	routeName: 'plan-main',
}

const DEFAULT_UPSELL_CONFIG: { routeNames: UpsellRoute[] } = {
	routeNames: ['upsell-static-subscription-activated', 'upsell-f'],
}

// Generic helper to get config value with fallback
const getConfigValue = <T extends object, K extends keyof T>(obj: T | undefined, key: K, defaultValue: T[K]): T[K] => {
	return obj && key in obj ? obj[key] : defaultValue
}

// Helper functions to get config values with fallbacks
export const getPaywallRouteName = (config: AppAreaConfig): string =>
	getConfigValue(config.paywall, 'routeName', DEFAULT_PAYWALL_CONFIG.routeName)

export const getUpsellRouteNames = (config: AppAreaConfig): string[] =>
	getConfigValue(config.upsell, 'routeNames', DEFAULT_UPSELL_CONFIG.routeNames)

/**
 * True when `path` is the landing entry of an OB-engine landing (DEV-34114).
 *
 * OB-engine landings live on an alias of the funnel route — the route record carries no
 * `meta.isLanding`, so "this is a landing entry" is derived by comparing the path against
 * `landing.route.path`. Case-insensitive and trailing-slash tolerant, because vue-router
 * matches routes non-strictly (`/Walking-F/` still resolves the `/walking-f` record).
 */
export function isObLandingAliasPath(appAreaConfig: AppAreaConfig | null | undefined, path: string): boolean {
	if (appAreaConfig?.landing?.engine !== 'ob') return false
	const landingPath = appAreaConfig.landing.route?.path
	if (!landingPath) return false
	const normalize = (p: string) => p.replace(/\/+$/, '').toLowerCase()
	return normalize(path) === normalize(landingPath)
}

// Route generation functions
function generateRoutesFromAppArea(appAreaConfig: AppAreaConfig) {
	const routes: Array<{
		path: string
		name: string
		component: () => Promise<unknown>
		alias?: string
		meta?: { isLanding?: boolean }
	}> = []

	const isObLanding = appAreaConfig.landing?.engine === 'ob'

	// OB-engine landing (DEV-34114): no standalone landing record — the landing screens live
	// inside the funnel config, so the funnel route below covers the landing path via `alias`.
	if (appAreaConfig.landing?.route && !isObLanding) {
		routes.push({
			path: appAreaConfig.landing.route.path,
			name: appAreaConfig.landing.route.name,
			component: () => import('@/modules/Landing/LandingView.vue'),
			meta: {
				isLanding: true,
			},
		})
	}

	if (appAreaConfig.funnel?.route) {
		routes.push({
			path: appAreaConfig.funnel.route.path,
			name: appAreaConfig.funnel.route.name,
			// Every funnel route renders the SAME component — OB-landing areas included.
			// vue-router reuses the component instance across funnel records only when their
			// identity matches, and SurveyManager's cross-funnel redirect chain
			// (prevFunnelTypeData in data()) relies on that reuse. OB-landing areas branch to
			// the legacy landing INSIDE OnboardingView (useLegacyLandingFallback), not via a
			// different route component.
			component: () => import('@/modules/Onboarding/OnboardingView.vue'),
			// Bare landing path (e.g. /walking-f) as an alias of the funnel route: the entry
			// URL is preserved while the funnel renders on it (no landing record to match).
			...(isObLanding && appAreaConfig.landing?.route ? { alias: appAreaConfig.landing.route.path } : {}),
		})
	}

	return routes
}

export function generateDomainConfig(
	appAreas: AppAreaConfig[],
	manualPathnames?: { [pathname: string]: AppAreaConfig },
): DomainConfig {
	const config: DomainConfig = { ...manualPathnames }

	for (const appArea of appAreas) {
		if (appArea.landing?.route?.path) {
			const pathname = appArea.landing.route.path
			config[pathname] = appArea
		}
	}

	return config
}

export function funnelRoutes(domainPathnames?: DomainConfig) {
	const areas = domainPathnames
		? [
				...new Map(
					Object.values(domainPathnames)
						.filter(isAppAreaConfig)
						.map((a) => [a.appArea, a]),
				).values(),
			]
		: APP_AREAS

	const allRoutes: Array<{
		path: string
		name: string
		component: () => Promise<unknown>
		alias?: string
		meta?: { isLanding?: boolean }
	}> = []

	areas.forEach((appAreaConfig) => {
		const routes = generateRoutesFromAppArea(appAreaConfig)
		allRoutes.push(...routes)
	})

	return allRoutes
}
